import type { TriBackendTypes, TriAnonymousBackendTypes } from '../../tri-backend/tri-backend';
import type { HazWasteBackendTypes } from '../../hazwaste-backend/hazwaste-backend';
import type { ComplianceBackendTypes } from '../../compliance-backend/compliance-backend';
import { auth } from '../../cross-app-frontend/firebase';

/** Shared implementation behind `backendRequest` and its per-app siblings: attaches the
 *  current user's auth token, gzip-compresses large payloads, and normalizes error handling. */
async function authenticatedBackendRequest<Response>(basePath: string, name: string, args: unknown): Promise<Response> {
  const token = await auth.currentUser?.getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const jsonString = JSON.stringify(args);

  // Compress requests over 1MB in size:
  let body: string | Blob = jsonString;
  if (jsonString.length > 1024 * 1024) {
    headers['Content-Encoding'] = 'gzip';
    body = await new Response(new Blob([jsonString]).stream().pipeThrough(new CompressionStream('gzip'))).blob();
  }

  const response: { status: 'error'; error: string } | { status: 'success'; response: Response } = await fetch(
    basePath + name,
    {
      method: 'POST',
      headers,
      body,
    },
  )
    .then(async (httpResponse) => {
      const responseText = await httpResponse.text();
      try {
        return JSON.parse(responseText) as
          | { status: 'error'; error: string }
          | { status: 'success'; response: Response };
      } catch (error) {
        const detail = responseText.replace(/\s+/g, ' ').trim().slice(0, 500);
        throw new Error(`HTTP ${httpResponse.status} ${httpResponse.statusText}: ${detail || 'Empty response body.'}`, {
          cause: error,
        });
      }
    })
    .catch((e) => {
      console.error(`Backend request to ${basePath}${name} failed`, args, e);
      throw new Error(
        `Backend request to ${basePath}${name} failed${e instanceof Error ? ` with error: ${e.message}` : ''}.`,
        { cause: e },
      );
    });

  if (response.status === 'error') {
    console.error(response);
    throw new Error(response.error);
  } else {
    return response.response;
  }
}

export async function backendRequest<Name extends keyof TriBackendTypes>(
  name: Name,
  args: TriBackendTypes[Name]['request'],
): Promise<TriBackendTypes[Name]['response']> {
  return authenticatedBackendRequest<TriBackendTypes[Name]['response']>('/api/tri/', name, args);
}

export async function hazWasteBackendRequest<Name extends keyof HazWasteBackendTypes>(
  name: Name,
  args: HazWasteBackendTypes[Name]['request'],
): Promise<HazWasteBackendTypes[Name]['response']> {
  return authenticatedBackendRequest<HazWasteBackendTypes[Name]['response']>('/api/hazwaste/', name, args);
}

export async function complianceBackendRequest<Name extends keyof ComplianceBackendTypes>(
  name: Name,
  args: ComplianceBackendTypes[Name]['request'],
): Promise<ComplianceBackendTypes[Name]['response']> {
  return authenticatedBackendRequest<ComplianceBackendTypes[Name]['response']>('/api/compliance/', name, args);
}

export async function backendAnonymousRequest<Name extends keyof TriAnonymousBackendTypes>(
  name: Name,
  args: TriAnonymousBackendTypes[Name]['request'],
): Promise<TriAnonymousBackendTypes[Name]['response']> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const jsonString = JSON.stringify(args);

  // Compress requests over 1MB in size:
  let body: string | Blob = jsonString;
  if (jsonString.length > 1024 * 1024) {
    headers['Content-Encoding'] = 'gzip';
    body = await new Response(new Blob([jsonString]).stream().pipeThrough(new CompressionStream('gzip'))).blob();
  }

  const response:
    | { status: 'error'; error: string }
    | { status: 'success'; response: TriAnonymousBackendTypes[Name]['response'] } = await fetch('/api/tri/' + name, {
    method: 'POST',
    headers,
    body,
  })
    .then((r) => r.json())
    .catch((e) => {
      console.error(`Backend request to tri/${name} failed`, args, e);
      throw new Error(
        `Backend request to tri/${name} failed${e instanceof Error ? ` with error: ${e.message}` : ''}.`,
        { cause: e },
      );
    });

  if (response.status === 'error') {
    console.error(response);
    throw new Error(response.error);
  } else {
    return response.response;
  }
}
